import { useAppSelector } from 'app/hooks';
import { Collapse } from 'bootstrap';
import { ProvisionTimingProps } from 'models';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectTimingInfo } from 'store/slices/mainSlice';
import { AraAngleDownIcon } from 'theme/icons';
import { attrLang } from 'utils';
import SelectedOption from '../SelectedOption';
import TimingItem from '../TimingItem';

interface ChildTimingGroupProps {
  provisionTiming: ProvisionTimingProps;
  dishCode: string;
  orderIdx: number;
  groupCode?: string;
  optionCode?: string;
}

const ChildTimingGroup = ({ provisionTiming, dishCode, orderIdx, groupCode, optionCode }: ChildTimingGroupProps) => {
  const { t } = useTranslation();
  const timingInfo = useAppSelector(selectTimingInfo);

  const timingInfoId = provisionTiming.timing_info_id;
  const timingDetail = timingInfo[timingInfoId];
  const servingPattern = timingDetail?.serving_pattern || [];

  const timingTitle = attrLang(timingDetail, 'title');

  const defaultRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  const [collapseClass] = useState<string>('');
  const [isShowing, setIsShowing] = useState<boolean>(false);

  const handleToggle = () => {
    toggleCollapse(defaultRef);
    toggleCollapse(selectedRef);
    setIsShowing((prev) => !prev);
  };

  useEffect(() => {
    updateCollapseState(selectedRef, defaultRef);
  }, [collapseClass]);

  const toggleCollapse = (ref: RefObject<HTMLDivElement>) => {
    if (ref.current) {
      const controller = Collapse.getOrCreateInstance(ref.current);
      controller.toggle();
    }
  };

  const updateCollapseState = (selectedRef: RefObject<HTMLDivElement>, defaultRef: RefObject<HTMLDivElement>) => {
    const hasSelectedTiming = provisionTiming.selected_timing !== '';

    if (hasSelectedTiming) {
      selectedRef.current?.classList.add('show');
      defaultRef.current?.classList.remove('show');
      setIsShowing(true);
    } else {
      selectedRef.current?.classList.remove('show');
      if (!defaultRef.current?.classList.contains('show')) {
        defaultRef.current?.classList.add('show');
      }
      setIsShowing(false);
    }
  };

  const ListOfSelectedOption: JSX.Element[] = [];

  Object.values(servingPattern).forEach((pattern) => {
    const code = Object.keys(pattern)[0];
    const timing = pattern[code];
    const label = attrLang(timing, 'msg');

    if (provisionTiming.selected_timing === code) {
      ListOfSelectedOption.push(<SelectedOption key={code} optionName={label} optionQuantity={1} />);
    }
  });

  const ListOfOption: (JSX.Element | null)[] = servingPattern.map((pattern) => {
    const code = Object.keys(pattern)[0];
    const timing = pattern[code];
    const timingName = attrLang(timing, 'msg');
    return (
      <TimingItem
        key={code}
        dishCode={dishCode}
        orderIdx={orderIdx}
        provisionTiming={provisionTiming}
        timingCode={code}
        timingName={timingName}
        isChild={true}
        groupCode={groupCode}
        optionCode={optionCode}
      />
    );
  });

  return (
    <div className="child-option-group" id={`option-group-${dishCode}-${orderIdx}-${timingTitle}`}>
      {ListOfOption.length > 0 && (
        <div className={`collapse-child-button ${collapseClass}`} onClick={handleToggle}>
          <div className="order-name">
            <span>{timingTitle}</span>
            <span className="order-name-nav">
              <AraAngleDownIcon className={`arrow-down ${isShowing ? 'show' : ''}`} />
            </span>
          </div>
        </div>
      )}
      <div className="collapse selected-list" ref={selectedRef}>
        <div className="list-child-group-wrapper" style={{ backgroundColor: 'white' }}>
          {ListOfSelectedOption.length > 0 ? (
            ListOfSelectedOption
          ) : (
            <p style={{ padding: '10px 8px 10px 16px', marginBottom: 0, backgroundColor: 'white' }}>
              {t('App.no_select_menu')}
            </p>
          )}
        </div>
      </div>
      <div className="collapse show default-list" ref={defaultRef}>
        <div className="list-child-group-wrapper">
          <div className="selected-options" style={{ paddingTop: '0rem', paddingBottom: '0rem' }}>
            <ul className="list-option-group">{ListOfOption}</ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildTimingGroup;
